import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Users, FileText, XCircle } from 'lucide-react';

// Import tab components
import { SkatersTab, EntriesTab, ScratchesTab } from './SkatersEntries';

export default function SkatersEntriesPage() {
  const [activeTab, setActiveTab] = useState('skaters');
  const [skaters, setSkaters] = useState([]);
  const [entries, setEntries] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Fetch current user
  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(res.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };
  
  // Fetch skaters
  const fetchSkaters = async () => {
    try {
      const res = await axios.get(`${API}/skaters`, { withCredentials: true });
      setSkaters(res.data);
    } catch (error) {
      console.error('Error fetching skaters:', error);
    }
  };
  
  // Fetch entries
  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/entries`, { withCredentials: true });
      setEntries(res.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };
  
  // Fetch clubs for dropdown (only "Club" type organisations)
  const fetchClubs = async () => {
    try {
      const res = await axios.get(`${API}/clubs`, { withCredentials: true });
      // Filter to only show clubs (not committees, national bodies, etc.)
      const clubsOnly = res.data.filter(c => c.organisation_type === 'Club');
      setClubs(clubsOnly);
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };
  
  // Fetch competitions
  const fetchCompetitions = async () => {
    try {
      const res = await axios.get(`${API}/competitions`, { withCredentials: true });
      setCompetitions(res.data);
    } catch (error) {
      console.error('Error fetching competitions:', error);
    }
  };
  
  // Fetch grades
  const fetchGrades = async () => {
    try {
      const res = await axios.get(`${API}/content/grades`, { withCredentials: true });
      setGrades(res.data);
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };
  
  // Fetch disciplines
  const fetchDisciplines = async () => {
    try {
      const res = await axios.get(`${API}/content/disciplines`, { withCredentials: true });
      setDisciplines(res.data);
    } catch (error) {
      console.error('Error fetching disciplines:', error);
    }
  };
  
  useEffect(() => {
    fetchCurrentUser();
    fetchSkaters();
    fetchEntries();
    fetchClubs();
    fetchCompetitions();
    fetchGrades();
    fetchDisciplines();
  }, []);
  
  // Tab configuration
  const tabs = [
    { id: 'skaters', label: 'Skaters', icon: Users },
    { id: 'entries', label: 'Entries', icon: FileText },
    { id: 'scratches', label: 'Scratches', icon: XCircle },
  ];
  
  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#2D4A3E' }}>Skaters & Entries</h1>
        <p className="text-muted-foreground mt-1">Manage skater records and competition entries</p>
      </div>
      
      {/* Tab Navigation */}
      <div className="border-b border-stone-200">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isDisabled = tab.disabled;
            return (
              <button
                key={tab.id}
                onClick={() => !isDisabled && setActiveTab(tab.id)}
                disabled={isDisabled}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  isActive 
                    ? 'border-green-700 text-green-800 font-medium' 
                    : isDisabled
                    ? 'border-transparent text-stone-300 cursor-not-allowed'
                    : 'border-transparent text-muted-foreground hover:text-green-700 hover:border-green-300'
                }`}
                style={isActive ? { color: '#2D4A3E' } : {}}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {isDisabled && <span className="text-xs bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">Coming Soon</span>}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Tab Content */}
      <div>
        {activeTab === 'skaters' && (
          <SkatersTab 
            skaters={skaters}
            setSkaters={setSkaters}
            fetchSkaters={fetchSkaters}
            clubs={clubs}
            currentUser={currentUser}
          />
        )}
        
        {activeTab === 'entries' && (
          <EntriesTab 
            entries={entries}
            setEntries={setEntries}
            fetchEntries={fetchEntries}
            skaters={skaters}
            fetchSkaters={fetchSkaters}
            competitions={competitions}
            grades={grades}
            disciplines={disciplines}
            clubs={clubs}
            currentUser={currentUser}
          />
        )}

        {activeTab === 'scratches' && (
          <ScratchesTab
            entries={entries}
            competitions={competitions}
          />
        )}
      </div>
    </div>
  );
}
